import { useState } from "react";
import { getApiInstance } from "../../utils/api";
import { nombreFicheroDescarga } from "../../utils/descargas";
import { useZone } from "../../context/ZoneContext";
import { useMoneda } from "../../context/MonedaContext";
import {
    INDICADORES,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
    trasladarFiltro,
} from "./indicadores/definiciones";

const pad2 = (n) => String(n).padStart(2, "0");

function timestampUTCYYYYMMDDHHmmss(date = new Date()) {
    const Y = date.getUTCFullYear();
    const M = pad2(date.getUTCMonth() + 1);
    const D = pad2(date.getUTCDate());
    const h = pad2(date.getUTCHours());
    const m = pad2(date.getUTCMinutes());
    const s = pad2(date.getUTCSeconds());
    return `${Y}${M}${D}${h}${m}${s}`;
}

const codZonaLegalMap = { 1: 1, 2: 2, 3: 2 };

/** Devuelve { codZona, codZonaLegal } si el quick filter de Zona del FilterBar
 * tiene una única zona seleccionada; en otro caso {} (se usa la zona de contexto). */
export function zonaExportFromFilterState(filterState) {
    const zonas = (filterState?.quick?.codZona ?? [])
        .map((z) => Number(z))
        .filter((z) => z === 1 || z === 2 || z === 3);
    const unique = [...new Set(zonas)];
    if (unique.length !== 1) return {};
    const codZona = unique[0];
    return {
        codZona,
        codZonaLegal: codZonaLegalMap[codZona] ?? codZona,
    };
}

export function buildInformeExportFilters({ selectedAnio, selectedTipoObra, selectedPais, selectedZonas } = {}) {
    const tipoObras =
        Array.isArray(selectedTipoObra) && selectedTipoObra.length
            ? selectedTipoObra
            : undefined;
    const zona = zonaExportFromFilterState(
        selectedZonas ? { quick: { codZona: selectedZonas } } : undefined
    );
    return {
        codFecha:
            selectedAnio != null && selectedAnio !== "all"
                ? selectedAnio
                : undefined,
        CodTipoObra:
            Array.isArray(selectedTipoObra) && selectedTipoObra.length === 1
                ? selectedTipoObra[0]
                : undefined,
        codPais:
            selectedPais != null && selectedPais !== "all"
                ? selectedPais
                : undefined,
        codsTipoObra: tipoObras,
        ...zona,
    };
}

/* Identificadores de las filas que la pantalla está mostrando. Se los pasa al
   exportar para que el Excel salga con esas y no con todo el informe. */
export function codsFilaDe(filas) {
    return (Array.isArray(filas) ? filas : [])
        .map((fila) => fila?.codFila)
        .filter((id) => id != null && id !== "");
}

/* Hojas del Informe Completo, en el orden de la plataforma antigua. `hoja` es la
   clave que espera el backend e `indicador` la pantalla de la que salen sus filas:
   las dos hojas de Abono Chatarra (importe y % de obras) comparten pantalla. */
const HOJAS_INFORME_COMPLETO = [
    { hoja: "segregacionTotal", indicador: "segregacionTotal" },
    { hoja: "datosValorizacion", indicador: "datosValorizacion" },
    { hoja: "toneladasValorizadas", indicador: "toneladasValorizadas" },
    { hoja: "costesGestion", indicador: "costesGestion" },
    { hoja: "abonoChatarra", indicador: "abonoChatarra" },
    { hoja: "abonoChatarraPorcentaje", indicador: "abonoChatarra" },
    { hoja: "ahorroSegregacion", indicador: "ahorroSegregacion" },
];

/* Las filas que vería la pantalla de cada hoja con el filtro de la pantalla desde
   la que se pide el informe. Se vuelven a pedir los datos de todos los indicadores,
   con la moneda activa, para que las siete hojas salgan de la misma carga. Si falla
   cualquier petición no se genera nada: un Excel con hojas a medias no sirve. */
async function codsFilaInformeCompleto(api, { filterState, excluirRaeeZeroWaste, moneda }) {
    const indicadores = Object.entries(INDICADORES);

    const [paises, tiposObra, ...respuestas] = await Promise.all([
        api.get(`/api/Paises/GetPaises`),
        api.get(`/api/ObrasTipo/GetAllObrasTipo`),
        ...indicadores.map(([, indicador]) =>
            api.get(indicador.datosUrl(moneda))
        ),
    ]);

    const mapPaises = mapaPaises(Array.isArray(paises.data) ? paises.data : []);
    const mapTiposObra = mapaTiposObra(
        Array.isArray(tiposObra.data) ? tiposObra.data : []
    );

    const codsFilaPorIndicador = {};
    indicadores.forEach(([clave, indicador], i) => {
        const filas = normalizarFilas(
            indicador.filasDe(respuestas[i].data),
            mapPaises,
            mapTiposObra
        );
        const traslado = trasladarFiltro(filterState, indicador, filas);
        codsFilaPorIndicador[clave] = codsFilaDe(
            filasVisibles(
                indicador,
                filas,
                traslado.filterState,
                excluirRaeeZeroWaste
            )
        );
    });

    return Object.fromEntries(
        HOJAS_INFORME_COMPLETO.map(({ hoja, indicador }) => [
            hoja,
            codsFilaPorIndicador[indicador],
        ])
    );
}

function descargarRespuesta(response, nombrePorDefecto) {
    const fileName = nombreFicheroDescarga(
        response.headers["content-disposition"],
        nombrePorDefecto
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export function useExcelExport({
    onError,
    singleUrl,
    resumenUrl,
    completoUrl,
} = {}) {
    const [isModalExcelOpen, setIsModalExcelOpen] = useState(false);
    const [exportando, setExportando] = useState(null);
    const { selectedZone } = useZone();
    const { moneda } = useMoneda();

    /* La zona del filtro si hay una sola elegida; si no, la de contexto. */
    const zonaParaExport = (filters = {}) => {
        const contextCodZona = selectedZone?.value ?? "0";
        const contextCodZonaLegal =
            codZonaLegalMap[Number(contextCodZona)] ?? contextCodZona ?? "0";

        return {
            codZona: filters.codZona != null ? filters.codZona : contextCodZona,
            codZonaLegal:
                filters.codZonaLegal != null
                    ? filters.codZonaLegal
                    : contextCodZonaLegal,
        };
    };

    const handleExportToExcel = async (tipo = "single", filters = {}) => {
        if (exportando) return;
        setExportando(tipo);
        const api = getApiInstance();
        try {
let apiUrl;
        if (singleUrl) {
            apiUrl =
                typeof singleUrl === "function"
                    ? singleUrl(tipo, filters)
                    : singleUrl;
        } else if (tipo === "resumen") {
            apiUrl =
                typeof resumenUrl === "function"
                    ? resumenUrl(tipo, filters)
                    : resumenUrl ??
                      "api/Informe/CreateReportExcelCompletoAgrupado";
        } else {
            apiUrl =
                typeof completoUrl === "function"
                    ? completoUrl(tipo, filters)
                    : completoUrl ?? "api/Informe/CreateReportExcelCompleto";
        }

            const { codZona, codZonaLegal } = zonaParaExport(filters);

            const query = new URLSearchParams({
                ...(filters.CodObra != null
                    ? { CodObra: filters.CodObra }
                    : {}),
                ...(filters.CodProyecto != null
                    ? { CodProyecto: filters.CodProyecto }
                    : {}),
                ...(filters.codFecha != null
                    ? { codFecha: filters.codFecha }
                    : {}),
                ...(filters.CodTipoObra != null
                    ? { CodTipoObra: filters.CodTipoObra }
                    : {}),
                codZona,
                codZonaLegal,
                // La misma moneda que se está viendo, para que el Excel no salga
                // en una distinta de la pantalla.
                ...(moneda ? { moneda } : {}),
                ...(filters.codPais ? { codPais: filters.codPais } : {}),
                ...(filters.codAnio ? { codAnio: filters.codAnio } : {}),
            });

            const knownKeys = new Set([
                "CodObra",
                "CodProyecto",
                "codFecha",
                "CodTipoObra",
                "codPais",
                "codAnio",
                "codZona",
                "codZonaLegal",
                // Va en el cuerpo, no en la query: son tantas como filas se vean.
                "codsFila",
            ]);
            Object.entries(filters).forEach(([key, value]) => {
                if (
                    !knownKeys.has(key) &&
                    value != null &&
                    value !== "" &&
                    value !== "all"
                ) {
                    if (Array.isArray(value)) {
                        value.forEach((item) => query.append(key, item));
                    } else {
                        query.set(key, value);
                    }
                }
            });

            const finalUrl = `${apiUrl}?${query.toString()}`;

            /* Los identificadores de las filas que se están viendo, ya filtradas. El
               backend monta el Excel solo con ellas, así que pantalla y fichero
               coinciden por construcción.

               null (la pantalla no los manda) y [] (los manda y no hay ninguna fila
               visible) no son lo mismo: con null el endpoint vuelca todo como hasta
               ahora, con [] el Excel sale vacío. */
            const codsFila = Array.isArray(filters.codsFila)
                ? filters.codsFila.filter((id) => id != null && id !== "")
                : null;

            const response = await api.post(finalUrl, codsFila, {
                responseType: "blob",
            });

            descargarRespuesta(
                response,
                `Informe_${timestampUTCYYYYMMDDHHmmss()}.xlsx`
            );
        } catch (err) {
            console.error(err);
            if (onError) {
                onError(
                    "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
                );
            }
        } finally {
            setExportando(null);
        }
    };

    /* Informe Completo: un Excel con las siete hojas de indicadores, cada una con el
       filtro de esta pantalla puesto en la pantalla de esa hoja. El backend monta
       cada hoja con el mismo export que el botón individual de su pantalla, a partir
       de estas filas. */
    const handleExportInformeCompleto = async ({
        filterState,
        excluirRaeeZeroWaste = false,
    } = {}) => {
        if (exportando) return;
        setExportando("completo");
        try {
            // Los indicadores con importes no se cargan sin moneda.
            if (!moneda) throw new Error("No hay moneda seleccionada.");

            const api = getApiInstance();
            const codsFila = await codsFilaInformeCompleto(api, {
                filterState,
                excluirRaeeZeroWaste,
                moneda,
            });

            const { codZona, codZonaLegal } = zonaParaExport(
                zonaExportFromFilterState(filterState)
            );
            const query = new URLSearchParams({
                codZona,
                codZonaLegal,
                moneda,
                excluirRaeeZeroWaste: String(Boolean(excluirRaeeZeroWaste)),
            });

            const response = await api.post(
                `api/Informe/CreateReportExcelIndicadoresUnificados?${query.toString()}`,
                codsFila,
                { responseType: "blob" }
            );

            descargarRespuesta(
                response,
                `Indicadores_${timestampUTCYYYYMMDDHHmmss()}.xlsx`
            );
        } catch (err) {
            console.error(err);
            if (onError) {
                onError(
                    "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
                );
            }
        } finally {
            setExportando(null);
        }
    };

    return {
        isModalExcelOpen,
        openModal: () => setIsModalExcelOpen(true),
        closeModal: () => setIsModalExcelOpen(false),
        handleExportToExcel,
        handleExportInformeCompleto,
        exportando,
    };
}
